import TextUtil from 'diagram-js/lib/util/Text';
import { ModelingUtil } from '@ispm/shared/bpmn-io/modeling-util';
import { NodeType } from '@ispm/shared/bpmn-io/node-type';
import { clone } from 'lodash';
import { StringUtils } from '@ispm/shared/utils/String.utils';

const DEFAULT_FONT_SIZE = 12;
const LINE_HEIGHT_RATIO = 1.2;

const MIN_TEXT_ANNOTATION_HEIGHT = 30;


export class CustomTextRenderer {
    private readonly defaultStyle;
    private readonly externalStyle;
    private textUtil;

    constructor(config) {
        this.defaultStyle = Object.assign({
            fontFamily: 'Arial, sans-serif',
            fontSize: DEFAULT_FONT_SIZE,
            fontWeight: 'normal',
            lineHeight: LINE_HEIGHT_RATIO
        }, config && config.defaultStyle || { });

        const fontSize = parseInt(this.defaultStyle.fontSize, 10) ;

        this.externalStyle = Object.assign({ }, this.defaultStyle, {
            fontSize: fontSize
        }, config && config.externalStyle || { });

        this.textUtil = new TextUtil({
            align: this.defaultStyle.align,
            padding: this.defaultStyle.padding,
            style: this.defaultStyle
        });
    }

    /**
     * Get the new bounds of an externally rendered,
     * layouted label.
     *
     * @param  {Bounds} bounds
     * @param  {String} text
     *
     * @return {Bounds}
     */
    getExternalLabelBounds(bounds, text) {

        const layoutedDimensions = this.textUtil.getDimensions(text, {
            box: {
                width: 90,
                height: 30,
                x: bounds.width / 2 + bounds.x,
                y: bounds.height / 2 + bounds.y
            },
            style: this.externalStyle
        });

        // resize label shape to fit label text
        return {
            x: Math.round(bounds.x + bounds.width / 2 - layoutedDimensions.width / 2),
            y: Math.round(bounds.y),
            width: Math.ceil(layoutedDimensions.width),
            height: Math.ceil(layoutedDimensions.height)
        };

    }

    getExternalLabelBoundsByInput(element, text, dimensions) {

        const options = {
            padding: undefined,
            box: {
                width: dimensions.width,
                height: dimensions.height,
                x: element.width / 2 + element.x,
                y: element.height / 2 + element.y
            },
            style: this.externalStyle
        };
        this.appendPaddingByNodeType(element, options);
        const layoutedDimensions = this.textUtil.getDimensions(text, options);


        // resize label shape to fit label text
        return {
            x: Math.round(element.x + element.width / 2 - layoutedDimensions.width / 2),
            y: Math.round(element.y),
            width: Math.ceil(layoutedDimensions.width),
            height: Math.ceil(layoutedDimensions.height)
        };

    }

    private appendPaddingByNodeType(element, options) {
        const nodeType = ModelingUtil.getNodeType(element);
        if (nodeType) {
            const nodePadding = this.defaultStyle.node_padding;
            const nodeTypeValue = NodeType[nodeType];
            if (nodePadding[nodeTypeValue]) {
                const defaultPadding = clone(this.defaultStyle.padding);
                options.padding = Object.assign(defaultPadding, nodePadding[nodeTypeValue]);
            }
        }
    }

    /**
     * Get the new bounds of text annotation.
     *
     * @param  {Bounds} bounds
     * @param  {String} text
     *
     * @return {Bounds}
     */
    getTextAnnotationBounds(bounds, text) {

        const layoutedDimensions = this.textUtil.getDimensions(text, {
            box: bounds,
            style: this.defaultStyle,
            align: 'left-top',
            padding: 5
        });

        return {
            x: bounds.x,
            y: bounds.y,
            width: bounds.width,
            height: Math.max(MIN_TEXT_ANNOTATION_HEIGHT, Math.round(layoutedDimensions.height))
        };
    }

    /**
     * Create a layouted text element.
     *
     * @param {String} text
     * @param {Object} [options]
     *
     * @return {SVGElement} rendered text
     */
    createText(text, options) {
        const element = options.box;
        delete options.align;
        delete options.padding;
        delete options.style;
        this.appendPaddingByNodeType(element, options);
        // TODO temporary until we contribute to BPMN module and fix line splitting
        text = StringUtils.replaceHyphensByMinusChar(text);
        return this.textUtil.createText(text, options || { });
    }

    /**
     * Get default text style.
     */
    getDefaultStyle() {
        return this.defaultStyle;
    }

    /**
     * Get the external text style.
     */
    getExternalStyle() {
        return this.externalStyle;
    }

}

CustomTextRenderer.$inject = ['config.textRenderer'];
